/**
 * One failed request, rendered as the right one of the shared states.
 *
 * The mapping is `API_CONTRACT.md` §5's two rules applied once instead of on every
 * screen: branch on `code` and never on `message`, and show the server's own
 * sentence only where §5.1 marks it safe. Getting that wrong on one screen out of
 * nine is exactly what a shared component prevents.
 *
 *   403 of the four chain refusals  →  `PermissionDenied`, with no retry button.
 *                                      Pressing a button does not grant a
 *                                      permission, and a retry under a correct
 *                                      refusal invites someone to hammer an
 *                                      endpoint that is answering properly.
 *   404 / 501                       →  a note saying the surface is not available
 *                                      in this build. **Not** an empty state: "no
 *                                      data" is a fact about a company's records
 *                                      and "this endpoint is not mounted" is a fact
 *                                      about this build, and `PRODUCT.md` §4 exists
 *                                      because collapsing two facts is how somebody
 *                                      acts on the wrong one.
 *   anything retryable              →  the note plus `RetryAction`.
 *   everything else                 →  the note, the code and the correlation id.
 *
 * The correlation id is on screen in every branch. It is the only thing a person can
 * quote that lets support find the request, and it costs one line.
 */

import type { ReactNode } from 'react';

import { useT } from '../i18n';
import { isRetryable, permissionDeniedCode, type WorkforceApiError } from '../api/errors';
import { PermissionDenied } from './PermissionDenied';
import { RetryAction } from './RetryAction';
import { StatusNote } from './StatusNote';
import { ErrorCodeLine } from './ErrorCodeLine';

/** Codes that mean "this build does not serve that", not "something went wrong". */
const NOT_AVAILABLE = new Set(['WORKFORCE_NOT_FOUND', 'WORKFORCE_NOT_IMPLEMENTED']);

export interface ApiErrorNoteProps {
  readonly error: WorkforceApiError;
  readonly onRetry?: () => void;
  /** What could not be read, in words. "điểm năng suất", "bản ghi thời gian". */
  readonly subject: string;
  /** Overrides the "not available in this build" sentence for a surface that has one. */
  readonly unavailableNote?: string;
  readonly testId?: string;
}

export function ApiErrorNote({
  error,
  onRetry,
  subject,
  unavailableNote,
  testId = 'api-error',
}: ApiErrorNoteProps): ReactNode {
  const t = useT();
  const denied = permissionDeniedCode(error);
  if (denied !== null) {
    return <PermissionDenied code={denied} correlationId={error.correlationId} testId={testId} />;
  }

  const unavailable = NOT_AVAILABLE.has(error.code);
  const retryable = !unavailable && isRetryable(error) && onRetry !== undefined;

  return (
    <StatusNote
      severity={unavailable ? 'info' : 'medium'}
      title={
        unavailable
          ? t('error.unavailable.title', { subject })
          : t('error.failed.title', { subject })
      }
      testId={testId}
      {...(retryable
        ? {
            actions: (
              <RetryAction
                onRetry={onRetry}
                retryAfterSeconds={error.retryAfterSeconds}
                testId={`${testId}-retry`}
              />
            ),
          }
        : {})}
    >
      <p>
        {unavailable
          ? (unavailableNote ?? t('error.unavailable.body'))
          : error.userSafe
            ? error.message
            : t('common.unknownError')}
      </p>
      <ErrorCodeLine code={error.code} correlationId={error.correlationId} />
    </StatusNote>
  );
}
